/**
 * Reference Data Utilities
 * Client-side utilities for working with external reference data API
 * 
 * Security: Calls external REST API with CORS support
 * Available to both authenticated users and guests for UX (address lookup, etc.)
 */

// Configuration - can be moved to environment variables or config file later
const REFERENCE_API_CONFIG = {
  // Simple API Gateway URL (new simple deployment)
  apiBaseUrl: 'https://dsps4h9kz5.execute-api.us-east-1.amazonaws.com/prod/v1',
  
  // Old API Gateway URL (SAM deployment - may be broken)
  // apiBaseUrl: 'https://ib9zfcms19.execute-api.us-east-1.amazonaws.com/prod/v1',
  
  // CloudFront URL (stage/version handled by CloudFront Origin Path)
  // apiBaseUrl: 'https://d3w0hzbs3xv5o0.cloudfront.net',
  
  // Timeout for API calls
  timeout: 10000,
  
  // Default headers
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
}

// Cache for reference data (5-minute client-side cache)
const cache = new Map()
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

/**
 * Generic fetch wrapper with timeout and error handling
 */
async function fetchWithTimeout(url, options = {}) {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), REFERENCE_API_CONFIG.timeout)
  
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...REFERENCE_API_CONFIG.headers,
        ...options.headers
      },
      signal: controller.signal
    })
    
    clearTimeout(timeoutId)
    
    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`)
    }
    
    return await response.json()
  } catch (error) {
    clearTimeout(timeoutId)
    if (error.name === 'AbortError') {
      throw new Error('API request timed out')
    }
    throw error
  }
}

/**
 * Generic cache wrapper
 */
function withCache(key, fetchFn) {
  return async (...args) => {
    const cacheKey = `${key}-${JSON.stringify(args)}`
    const cached = cache.get(cacheKey)
    
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data
    }
    
    try {
      const data = await fetchFn(...args)
      cache.set(cacheKey, { data, timestamp: Date.now() })
      return data
    } catch (error) {
      // Return cached data if available, even if expired
      if (cached) {
        return cached.data
      }
      throw error
    }
  }
}

/**
 * Get neighborhood by ID (hierarchical ID format)
 */
export const getNeighborhood = withCache('neighborhood', async (id) => {
  const url = `${REFERENCE_API_CONFIG.apiBaseUrl}/neighborhoods/${encodeURIComponent(id)}`
  return await fetchWithTimeout(url)
})

/**
 * Get neighborhood by state, city, and slug
 */
export const getNeighborhoodByPath = withCache('neighborhood-path', async (state, city, slug) => {
  const url = `${REFERENCE_API_CONFIG.apiBaseUrl}/neighborhoods/${encodeURIComponent(state)}/${encodeURIComponent(city)}/${encodeURIComponent(slug)}`
  return await fetchWithTimeout(url)
})

/**
 * Get neighborhoods by state and city
 */
export const getNeighborhoodsByLocation = withCache('neighborhoods-location', async (state, city = null) => {
  const params = new URLSearchParams({ state })
  if (city) {
    params.append('city', city)
  }
  
  const url = `${REFERENCE_API_CONFIG.apiBaseUrl}/neighborhoods?${params.toString()}`
  return await fetchWithTimeout(url)
})

/**
 * Get neighborhoods by ZIP code
 */
export const getNeighborhoodsByZip = withCache('neighborhoods-zip', async (zipCode) => {
  const url = `${REFERENCE_API_CONFIG.apiBaseUrl}/zip/${encodeURIComponent(zipCode)}/neighborhoods`
  return await fetchWithTimeout(url)
})

/**
 * Get all states
 */
export const getStates = withCache('states', async () => {
  const url = `${REFERENCE_API_CONFIG.apiBaseUrl}/states`
  return await fetchWithTimeout(url)
})

/**
 * Get cities by state
 */
export const getCitiesByState = withCache('cities-state', async (state) => {
  const url = `${REFERENCE_API_CONFIG.apiBaseUrl}/states/${encodeURIComponent(state)}/cities`
  return await fetchWithTimeout(url)
})

/**
 * Get ZIP code details
 */
export const getZipCodeDetails = withCache('zipcode-details', async (zipCode) => {
  const url = `${REFERENCE_API_CONFIG.apiBaseUrl}/zip/${encodeURIComponent(zipCode)}`
  return await fetchWithTimeout(url)
})

/**
 * Get all regions
 */
export const getRegions = withCache('regions', async () => {
  const url = `${REFERENCE_API_CONFIG.apiBaseUrl}/regions`
  return await fetchWithTimeout(url)
})

/**
 * Get region by slug
 */
export const getRegionBySlug = withCache('region-slug', async (slug) => {
  const url = `${REFERENCE_API_CONFIG.apiBaseUrl}/regions/${encodeURIComponent(slug)}`
  return await fetchWithTimeout(url)
})

/**
 * Format neighborhood for display
 */
export function formatNeighborhoodDisplay(neighborhood) {
  if (!neighborhood) return 'Unknown Location'
  
  return `${neighborhood.name} (${neighborhood.city}, ${neighborhood.state})`
}

/**
 * Format neighborhood path for display
 */
export function formatNeighborhoodPath(neighborhood) {
  if (!neighborhood) return 'Unknown Neighborhood (Location Unknown)';
  
  // Handle path strings like "Il/Chicago/Avondale"
  if (typeof neighborhood === 'string' && neighborhood.includes('/')) {
    const parts = neighborhood.split('/');
    if (parts.length === 3) {
      const [state, city, neighborhoodName] = parts;
      // Convert to readable format
      const formatName = (str) => str.split('-').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');
      
      const formattedNeighborhood = formatName(neighborhoodName);
      const formattedCity = formatName(city);
      const formattedState = state.toUpperCase();
      
      return `${formattedNeighborhood} (${formattedCity}, ${formattedState})`;
    }
  }
  
  // Handle neighborhood objects
  if (typeof neighborhood === 'object' && neighborhood.state && neighborhood.city && neighborhood.name) {
    const city = neighborhood.city.charAt(0).toUpperCase() + neighborhood.city.slice(1);
    const state = neighborhood.state.toUpperCase();
    return `${neighborhood.name} (${city}, ${state})`;
  }
  
  // Fallback
  return 'Unknown Neighborhood (Location Unknown)';
}

/**
 * Parse neighborhood path from URL
 */
export function parseNeighborhoodPath(path) {
  const parts = path.split('/')
  if (parts.length !== 3) return null
  
  return {
    state: parts[0],
    city: parts[1].replace(/-/g, ' '),
    neighborhood: parts[2].replace(/-/g, ' ')
  }
}

/**
 * Get neighborhood URL
 */
export function getNeighborhoodUrl(neighborhood) {
  if (!neighborhood) return '#';
  
  // Handle path strings like "Il/Chicago/Avondale"
  if (typeof neighborhood === 'string' && neighborhood.includes('/')) {
    const parts = neighborhood.split('/');
    if (parts.length === 3) {
      const [state, city, neighborhoodName] = parts;
      // Normalize the URL parts
      const normalizedState = state.charAt(0).toUpperCase() + state.slice(1).toLowerCase();
      const normalizedCity = city.toLowerCase().replace(/\s+/g, '-');
      const normalizedNeighborhood = neighborhoodName.toLowerCase().replace(/\s+/g, '-');
      return `/neighborhood/${normalizedState}/${normalizedCity}/${normalizedNeighborhood}`;
    }
  }
  
  // Handle neighborhood objects with state/city/slug properties
  if (typeof neighborhood === 'object' && neighborhood.state && neighborhood.city) {
    const state = neighborhood.state.charAt(0).toUpperCase() + neighborhood.state.slice(1).toLowerCase();
    const city = neighborhood.city.toLowerCase().replace(/\s+/g, '-');
    const slug = (neighborhood.slug || neighborhood.name || '').toLowerCase().replace(/\s+/g, '-');
    return `/neighborhood/${state}/${city}/${slug}`;
  }
  
  // Fallback
  return '#';
}

/**
 * Clear cache (useful for testing or data updates)
 */
export function clearReferenceDataCache() {
  cache.clear()
}

/**
 * Update API configuration (useful for switching between direct API Gateway and CloudFront)
 */
export function updateApiConfig(newConfig) {
  Object.assign(REFERENCE_API_CONFIG, newConfig)
}

/**
 * Get current API configuration
 */
export function getApiConfig() {
  return { ...REFERENCE_API_CONFIG }
}

/**
 * Preload common reference data
 */
export async function preloadReferenceData() {
  try {
    // Preload states (most commonly needed)
    await getStates()
  } catch (error) {
  }
}

/**
 * Switch to CloudFront URL (call this when CloudFront is working properly)
 */
export function switchToCloudFront() {
  updateApiConfig({
    apiBaseUrl: 'https://d3w0hzbs3xv5o0.cloudfront.net'
  })
}

/**
 * Switch to direct API Gateway URL (fallback)
 */
export function switchToDirectApi() {
  updateApiConfig({
    apiBaseUrl: 'https://dsps4h9kz5.execute-api.us-east-1.amazonaws.com/prod/v1'
  })
}

/**
 * Search neighborhoods by name, city, state, or zipcode (replaces GraphQL version)
 * @param {string} query - Search term
 * @param {Object} options - Search options
 * @param {number} options.limit - Maximum results to return (default: 10)
 * @returns {Promise<Array>} Array of neighborhood objects
 */
export async function searchNeighborhoods(query, options = {}) {
  const { limit = 10 } = options
  
  if (!query || query.length < 2) {
    return []
  }

  
  // Check if query is a zip code (5 digits)
  const isZipcode = /^\d{5}$/.test(query)
  
  if (isZipcode) {
    // Search by zipcode using reference API
    
    try {
      const neighborhoods = await getNeighborhoodsByZip(query)
      
      // Transform results and add zipcode flag
      const results = neighborhoods.slice(0, limit).map(n => ({
        ...n,
        isFromZipcode: true,
        originalZipcode: query
      }))
      
      return results
    } catch (error) {
      console.error('searchNeighborhoods: Error searching by zipcode:', error)
      return []
    }
  }
  
  // Search regular neighborhoods by name, city, state
  
  try {
    // For text search, we need to determine if it's a state or city search
    // First try to get all states to see if query matches a state
    let allNeighborhoods = []
    
    // Check if query looks like a state abbreviation
    if (query.length === 2 && /^[A-Z]{2}$/i.test(query)) {
      // State abbreviation - get all neighborhoods in that state
      try {
        allNeighborhoods = await getNeighborhoodsByLocation(query.toUpperCase())
      } catch (error) {
        return []
      }
    } else {
      // Try to parse as "City, State" format
      const cityStateMatch = query.match(/^(.+),\s*([A-Z]{2})$/i)
      if (cityStateMatch) {
        const [, city, state] = cityStateMatch
        try {
          allNeighborhoods = await getNeighborhoodsByLocation(state.toUpperCase(), city.trim())
        } catch (error) {
          return []
        }
      } else {
        // General search using the search endpoint
        try {
          const url = `${REFERENCE_API_CONFIG.apiBaseUrl}/neighborhoods/search?q=${encodeURIComponent(query)}&limit=${limit}`
          const searchResponse = await fetchWithTimeout(url)
          // Extract results array from the search response
          allNeighborhoods = searchResponse.results || []
        } catch (error) {
          return []
        }
      }
    }
    
    
    // For search endpoint results, use pre-sorted results from backend
    if (allNeighborhoods.length > 0 && allNeighborhoods[0].slug) {
      return allNeighborhoods.slice(0, limit)
    }
    
    // For location-based results, do client-side filtering
    const filtered = allNeighborhoods.filter(neighborhood => {
      const searchTerm = query.toLowerCase()
      return (
        neighborhood.name?.toLowerCase().includes(searchTerm) ||
        neighborhood.city?.toLowerCase().includes(searchTerm) ||
        neighborhood.state?.toLowerCase().includes(searchTerm) ||
        `${neighborhood.city}, ${neighborhood.state}`.toLowerCase().includes(searchTerm)
      )
    })
    
    
    // Sort results by relevance
    const sorted = filtered.sort((a, b) => {
      const searchTerm = query.toLowerCase()
      const aName = (a.name || '').toLowerCase()
      const bName = (b.name || '').toLowerCase()
      
      // Exact name matches first
      if (aName === searchTerm && bName !== searchTerm) return -1
      if (bName === searchTerm && aName !== searchTerm) return 1
      
      // Name starts with search term
      if (aName.startsWith(searchTerm) && !bName.startsWith(searchTerm)) return -1
      if (bName.startsWith(searchTerm) && !aName.startsWith(searchTerm)) return 1
      
      // Alphabetical order
      return aName.localeCompare(bName)
    }).slice(0, limit)
    
    return sorted
    
  } catch (error) {
    console.error('searchNeighborhoods: Error searching neighborhoods:', error)
    return []
  }
}

/**
 * Get display name for a neighborhood (handles both regular and zipcode neighborhoods)
 * @param {Object} neighborhood - Neighborhood object
 * @returns {string} Display name
 */
export function getNeighborhoodDisplayName(neighborhood) {
  if (!neighborhood) return 'Unknown Neighborhood'
  return neighborhood.name || neighborhood.neighborhoodName || 'Unknown Neighborhood'
}

/**
 * Get location text for a neighborhood
 * @param {Object} neighborhood - Neighborhood object
 * @returns {string} Location text (e.g., "Chicago, IL")
 */
export function getNeighborhoodLocationText(neighborhood) {
  if (!neighborhood) return 'Location Unknown'
  if (neighborhood.city && neighborhood.state) {
    return `${neighborhood.city}, ${neighborhood.state}`
  }
  return 'Location Unknown'
}

/**
 * Check if a string is a valid US zipcode
 * @param {string} value - Value to check
 * @returns {boolean} True if valid zipcode
 */
export function isZipcode(value) {
  return /^\d{5}$/.test(value)
}

/**
 * Sort neighborhoods by custom display order
 * @param {Array} neighborhoods - Array of neighborhood objects
 * @returns {Array} Sorted neighborhoods
 */
export function sortNeighborhoodsByCustomOrder(neighborhoods) {
  return neighborhoods.sort((a, b) => {
    // Primary sort: by displayOrder if available (lower numbers first)
    const orderA = a.displayOrder || a.display_order || 999999;
    const orderB = b.displayOrder || b.display_order || 999999;

    if (orderA !== orderB) {
      return orderA - orderB;
    }

    // Secondary sort: alphabetical by name
    return (a.name || '').localeCompare(b.name || '');
  });
}